import {Controller} from "@hotwired/stimulus"
import _ from "lodash"
import {
    SlickRowSelectionModel,
    SlickDataView,
    SlickGrid,
    SlickResizer,
    SlickCellSelectionModel,
    SlickCellCopyManager,
} from "slickgrid"

// ── Currency cell formatting ────────────────────────────────────────────────
// Numeric report cells used to ship as `data-controller="currency"` spans, so
// Stimulus instantiated a controller per *visible* cell (and again on every
// scroll as SlickGrid recycles rows) — the report grid's main bottleneck.
//
// Instead tabulator_amount_span now emits a compact sentinel placeholder:
//   \u0001$ amount \u0001 number_scale \u0001 comma_style \u0001 precision \u0001
// which the SlickGrid column formatter below expands and formats *once per
// render*. The logic mirrors currency_controller.js exactly (scale / comma /
// precision resolution, parentheses for negatives, displayZeroAs). A regex
// swap means it also works when the placeholder sits inside a drill-down <a>
// or is followed by a function-icon span.
const CUR_RE = /\u0001\$([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001/g
const CUR_SCALE_DIVISORS = {units: 1, thousands: 1000, lakhs: 100000, millions: 1000000, crores: 10000000}
const CUR_COMMA_STYLES = new Set(['indian', 'western'])
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// <meta> defaults are page-global and only change on a full reload, so read
// them once instead of per cell.
let _curMeta = null
function curMeta() {
    if (_curMeta) return _curMeta
    const m = (n) => document.querySelector(`meta[name="${n}"]`)?.content
    _curMeta = {
        mono: m('numericFontMonospace') === 'true',
        round: m('userRoundPreference') || 'units',
        comma: m('userCommaStyle') || 'indian',
        precision: m('userPrecision') || '',
        zeroAs: m('displayZeroAs') || ''
    }
    return _curMeta
}

const _intlCache = new Map()
function curIntl(locale, minFractionDigits) {
    const key = `${locale}|${minFractionDigits}`
    let f = _intlCache.get(key)
    if (!f) {
        f = new Intl.NumberFormat(locale, {minimumFractionDigits: minFractionDigits})
        _intlCache.set(key, f)
    }
    return f
}

const isBlankAttr = (v) => v == null || v === '' || v === 'null' || v === 'undefined'

function escapeHtmlAttribute(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

function currencyScale(value) {
    const candidate = String(value == null ? '' : value)
    return Object.prototype.hasOwnProperty.call(CUR_SCALE_DIVISORS, candidate) ? candidate : ''
}

function currencyCommaStyle(value) {
    const candidate = String(value == null ? '' : value)
    return CUR_COMMA_STYLES.has(candidate) ? candidate : ''
}

function currencyPrecision(value) {
    if (isBlankAttr(value)) return ''
    const parsed = Number(value)
    return Number.isInteger(parsed) ? String(Math.max(0, Math.min(8, parsed))) : ''
}

function platformReportApiPrefix(element) {
    const explicit = element.dataset.platformReportApiPrefix
    if (explicit) {
        const prefixUrl = new URL(explicit, window.location.origin)
        const pathname = prefixUrl.pathname.endsWith('/') ? prefixUrl.pathname : `${prefixUrl.pathname}/`
        if (prefixUrl.origin !== window.location.origin ||
            !['http:', 'https:'].includes(prefixUrl.protocol) ||
            prefixUrl.search || prefixUrl.hash ||
            !/^\/[^/]+\/p-api\/[^/]+\/reports\/$/.test(pathname)) {
            throw new Error('Invalid report action URL')
        }
        return pathname
    }

    const org = document.querySelector('meta[name="organizationSlug"]')?.content || ''
    const app = document.querySelector('meta[name="platformAppSlug"]')?.content || ''
    if (!org || !app) throw new Error('Invalid report action URL')
    return `/${encodeURIComponent(org)}/p-api/${encodeURIComponent(app)}/reports/`
}

function platformReportSourceId(element) {
    const sourceId = String(element.dataset.platformReportSourceId || '')
    if (!UUID_PATTERN.test(sourceId)) throw new Error('Invalid report action URL')
    return sourceId
}

function platformReportActionPathAllowed(pathname, element) {
    const base = `${platformReportApiPrefix(element)}${encodeURIComponent(platformReportSourceId(element))}`
    if (pathname === base) return true

    const suffix = pathname.slice(base.length)
    if (!pathname.startsWith(`${base}/`)) return false
    if (/^\/(?:drilldown|breakup|expand|large-data|filter-values|download|recompute)$/.test(suffix)) return true
    const argumentId = suffix.startsWith('/argument-options/') ? suffix.slice('/argument-options/'.length) : ''
    return UUID_PATTERN.test(argumentId)
}

// Platform ReportBlock actions are capabilities and must remain inside the
// exact current app report prefix. Story report grids predate p-api and use
// their own same-origin drilldown routes, so retain those without weakening
// the Platform boundary.
function reportActionUrl(raw, element) {
    const url = new URL(String(raw || ''), window.location.origin)
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid report action URL')
    }

    if (!element.classList.contains('platform-report-grid')) return url.toString()

    if (!platformReportActionPathAllowed(url.pathname, element)) throw new Error('Invalid report action URL')
    return url.toString()
}

function reportRequestFailed(message, reportId) {
    window.dispatchEvent(new CustomEvent('platform-report:error', {detail: {message, reportId}}))
}

// scaleOverride = the card's live data-report-scale (or null). Mirrors the
// resolution order in currency_controller.js.
// blackNegatives = report_template.other_options.black_negative_numbers — when
// true, negatives keep brackets but drop the red text colour.
function buildCurrencyHtml(amountStr, ns, cs, pr, scaleOverride, blackNegatives = false) {
    const meta = curMeta()
    const amt = parseFloat(amountStr)

    // scale: card override > per-cell number_scale > meta default
    const overrideScale = currencyScale(scaleOverride)
    const cellScale = currencyScale(ns)
    const scale = overrideScale || cellScale || currencyScale(meta.round) || 'units'

    // precision: an active scale override forces the meta/user precision
    let precStr = overrideScale ? '' : currencyPrecision(pr)
    if (precStr === '') precStr = currencyPrecision(meta.precision)
    const precision = (precStr === '') ? (scale === 'units' ? 0 : 2) : Number(precStr)

    const cellCommaStyle = currencyCommaStyle(cs)
    const commaStyle = cellCommaStyle || currencyCommaStyle(meta.comma) || 'indian'
    const locale = commaStyle === 'western' ? 'en-US' : 'en-IN'

    let text
    if (amt === 0 || isNaN(amt)) {
        text = meta.zeroAs === 'zero' ? '0' : '-'
    } else {
        const divisor = CUR_SCALE_DIVISORS[scale] || 1
        const rounded = parseFloat((Math.abs(amt) / divisor).toFixed(precision))
        const formatted = curIntl(locale, precision).format(rounded)
        text = amt < 0 ? `(${formatted})` : formatted
    }

    let cls = 'currency'
    if (amt < 0 && !blackNegatives) cls += ' text-red-600'
    if (meta.mono) cls += ' jetbrains-mono'
    // Keep the data-currency-* attrs (but NOT data-controller) so the
    // selection-sum bar can still read per-cell formatting from the span.
    const amountValue = Number.isFinite(amt) ? String(amt) : ''
    return `<span class="${cls}" data-currency-amount-value="${escapeHtmlAttribute(amountValue)}" data-currency-number-scale-value="${escapeHtmlAttribute(cellScale)}" data-currency-comma-style-value="${escapeHtmlAttribute(cellCommaStyle)}" data-currency-precision-value="${escapeHtmlAttribute(currencyPrecision(pr))}">${text}</span>`
}

// Expand any currency placeholder(s) inside a cell value. Non-currency values
// (plain text, links, blanks) are returned unchanged. `scaleOf` is a function
// so the card scale is read lazily — only for cells that actually need it.
function renderCellValue(value, scaleOf, blackNegatives = false) {
    if (typeof value !== 'string') return value == null ? '' : value
    if (value.indexOf('\u0001$') === -1) return value
    return value.replace(CUR_RE, (_m, amount, ns, cs, pr) => buildCurrencyHtml(amount, ns, cs, pr, scaleOf(), blackNegatives))
}

// The copy button copies `_copy_value`, which the server pre-rounded at the
// report's own scale. Once the card's scale dropdown overrides that, rebuild
// the figure from the cell's currency placeholder with the same scale and
// precision buildCurrencyHtml shows. Null means "no override / not currency",
// so the caller keeps its usual fallbacks.
function scaledCopyValue(value, scaleOverride) {
    const scale = currencyScale(scaleOverride)
    if (!scale || typeof value !== 'string') return null
    const match = /\u0001\$([^\u0001]*)\u0001/.exec(value)
    const amt = match ? parseFloat(match[1]) : NaN
    if (!Number.isFinite(amt)) return null

    const precStr = currencyPrecision(curMeta().precision)
    const precision = (precStr === '') ? (scale === 'units' ? 0 : 2) : Number(precStr)
    return parseFloat((amt / CUR_SCALE_DIVISORS[scale]).toFixed(precision))
}

export default class extends Controller {
    static targets = ['element', 'hitems',
        // Column show/hide panel (see the Expand/Collapse dropdown in _table_data.html.erb)
        'columnSearch', 'colRow', 'colCheckbox', 'selectAll', 'columnEmpty']

    static values = {
        headers: {type: String, default: ''},
        ogHeaders: {type: Array, default: []},
        groupNames: {type: Array, default: []},
        options: {type: Object, default: {}},
        containerName: {type: String, default: ''},
        frozenCount: {type: String, default: ''},
        fontSize: {type: String, default: 'small'},
        autoHeight: {type: Boolean, default: false},
        scrollBar: {type: Boolean, default: false},
        forceFit: {type: Boolean, default: false},
        blackNegatives: {type: Boolean, default: false},
        minHeight: {type: Number, default: 0}
    }

    connect() {
        this.scrollInterval = null;
        this.detailAborts = new Set()
        let isMobile = window.innerWidth <= 768
        // React ReportBlock cannot safely create/evaluate a window-var script during
        // concurrent rendering. Prefer its inert JSON payload; retain the legacy
        // window variable path for Story report ERB.
        const headersScript = this.element.querySelector('script[type="application/json"][data-slick-grid-headers]')
        let headers
        try { headers = headersScript ? JSON.parse(headersScript.textContent) : window[this.headersValue] } catch (_error) { headers = window[this.headersValue] }
        // Rows arrive as a JSON <script> tag (see _table_data.html.erb) instead
        // of a data-* attribute, to avoid HTML-attribute escaping bloat.
        const rowsScript = this.element.querySelector('script[type="application/json"][data-slick-grid-rows]')
        let data = JSON.parse(rowsScript ? rowsScript.textContent : '[]');
        let groupNames = this.groupNamesValue;
        let containerName = this.containerNameValue;
        let frozenCount = _.toNumber(this.frozenCountValue);
        if (isMobile) {
            frozenCount = -1
        }
        if (headers === undefined) {
            const scriptContent = this.element.getElementsByTagName('script')[0].innerHTML
            const script = document.createElement("script")
            script.text = scriptContent
            document.head.appendChild(script).parentNode.removeChild(script)
            headers = window[this.headersValue]
        }

        // The report card carries the live number scale (data-report-scale,
        // written by the scale dropdown). Currency cells format against it in
        // the formatters below, so a card re-render re-applies the new scale.
        const card = this.element.querySelector('.lz-report-card') || this.element
        const shadeDrilldown = !this.element.classList.contains('lz-report-worksheet')
        this.card = card
        const scaleOf = () => {
            const s = card.getAttribute('data-report-scale')
            return (s && s !== '' && s !== 'default') ? s : null
        }
        // Template other_options.black_negative_numbers — brackets stay, drop red.
        const blackNegatives = this.blackNegativesValue === true ||
            card.getAttribute('data-black-negatives') === 'true'

        var Slickformatter = function (row, cell, value, columnDef, dataContext) {
            var headerId = headers[cell].id
            let style = dataContext[`${headerId}_html_style`] || ''
            if (shadeDrilldown && dataContext.indent >= 1) {
                style += ` slick-table-bg-indent-${dataContext.indent}`
            }
            // if (dataContext.tree_link && dataContext.indent === 0 && !dataContext._collapsed) {
            //   style += ' slick-table-bg-indent-1'
            // }
            return {html: renderCellValue(value, scaleOf, blackNegatives), addClasses: style};
        }

        var TaskNameFormatter = function (row, cell, value, columnDef, dataContext) {
            if (value == null || dataContext === undefined) {
                return "";
            }
            value = renderCellValue(value, scaleOf, blackNegatives)
            let headerId = headers[cell].id
            let text = ''
            let spacer = ''
            if (dataContext["parent"] !== null) {
                spacer = "<span style='display:inline-block;height:1px;width:" + (15 * dataContext["indent"]) + "px'></span>";
            }
            let style = dataContext[`${headerId}_html_style`] || ''
            if (dataContext.tree_link) {
                if (dataContext._collapsed) {
                    text = spacer + " <span class='toggle cursor-pointer lz-caret-right-icon inline-block h-[10px] w-[14px]'></span>&nbsp;&nbsp;" + value;
                } else {
                    text = spacer + " <span class='cursor-pointer toggle lz-caret-down-icon inline-block h-[10px] w-[14px]'></span>&nbsp;&nbsp;" + value;
                    // if (dataContext.indent === 0) {
                    //   style += ' slick-table-bg-indent-1'
                    // }
                }
            } else if (dataContext["parent"] !== null) {
                text = spacer + "&nbsp;&nbsp;&nbsp;" + value;
            } else {
                text = spacer + value;
            }
            if (shadeDrilldown && dataContext.indent >= 1) {
                style += ` slick-table-bg-indent-${dataContext.indent}`
            }
            return {html: text, addClasses: style}
        };

        for (var i = 0; i < headers.length; i++) {
            if (i === 0) {
                headers[i]['formatter'] = TaskNameFormatter
            } else {
                headers[i]['formatter'] = Slickformatter
            }
        }

        let dataView;
        let grid;
        let columns = headers
        this.slickHeaders = headers
        // Active sort order, outermost column first. Shift+click on a header
        // appends to this list (see grid.onSort below) so rows can be ordered
        // by Segment -> Date -> Value instead of only the last column clicked.
        let sortCols = [{field: "title", dir: 1}];
        let colGroups = _.compact(_.map(columns, 'columnGroup'))

        let rowHeight = 28
        let topPanelRowHeight = 32

        if (this.fontSizeValue === 'medium') {
            rowHeight = 32
            topPanelRowHeight = 36
        } else if (this.fontSizeValue === 'large') {
            rowHeight = 36
            topPanelRowHeight = 40
        }

        let forceFit = this.forceFitValue
        let scrollBar = this.scrollBarValue


        var options = {
            enableCellNavigation: true,
            // Decorate selection without replacing report-defined cell colors.
            selectedCellCssClass: this.element.classList.contains('lz-report-worksheet') ? 'lz-report-cell-selected' : 'selected',
            enableTextSelectionOnCells: true,
            createPreHeaderPanel: true,
            showPreHeaderPanel: colGroups.length > 0,
            preHeaderPanelHeight: colGroups.length > 0 ? topPanelRowHeight : 0,
            topPanelHeight: topPanelRowHeight,
            headerRowHeight: topPanelRowHeight,
            rowHeight: rowHeight,
            enableColumnReorder: false,
            enableAutoSizeColumns: true,
            frozenColumn: frozenCount,
            forceFitColumns: forceFit, // Add this line
            autoHeight: this.autoHeightValue || this.optionsValue.auto_height === true,
            // Scroll one row at a time on arrow-key navigation instead of
            // jumping a full page and dropping the active cell at the top.
            emulatePagingWhenScrolling: false,
            // Multi-column sort: plain click sorts by one column (and clears
            // the rest), shift+click adds a column to the existing sort,
            // cmd/ctrl+click drops one. The numbered badge next to each arrow
            // shows that column's position in the sort order.
            multiColumnSort: true,
            numberedMultiColumnSort: true,
            sortColNumberInSeparateSpan: true
        };

        let CreateAddlHeaderRow = this.CreateAddlHeaderRow


        options = Object.assign(options, this.optionsValue);

        var myFilterDe = function (item) {

            if (item.parent != null) {
                var parent = this.items.find((ele) => ele.id === item.parent);

                while (parent) {
                    if (parent._collapsed) {
                        return false;
                    }
                    parent = this.items.find((ele) => ele.id === parent.parent);
                }
            }

            return true;
        }

        dataView = new SlickDataView({inlineFilters: true});
        dataView.beginUpdate();
        dataView.setItems(data);
        dataView.setFilter(myFilterDe);
        dataView.endUpdate();
        grid = new SlickGrid(this.elementTarget, dataView, columns, options);
        const cellSelectionModel = new SlickCellSelectionModel();
        grid.setSelectionModel(cellSelectionModel);

        // Excel-like aggregation bar: when one or more cells are selected, show
        // Count / Sum / Average of the numeric values in the selection.
        cellSelectionModel.onSelectedRangesChanged.subscribe((e, ranges) => {
            this.updateSelectionStats(ranges, grid, dataView);
        });

        // Register copy manager to enable Ctrl+C / Cmd+C clipboard copy
        const copyManager = new SlickCellCopyManager();
        grid.registerPlugin(copyManager);

        copyManager.onCopyCells.subscribe((e, args) => {
            const ranges = args.ranges;
            if (!ranges || ranges.length === 0) return;

            const rows = [];
            for (let i = 0; i < ranges.length; i++) {
                const range = ranges[i];
                for (let r = range.fromRow; r <= range.toRow; r++) {
                    const cellValues = [];
                    for (let c = range.fromCell; c <= range.toCell; c++) {
                        const cellNode = grid.getCellNode(r, c);
                        const val = cellNode ? cellNode.textContent.trim() : '';
                        cellValues.push(val);
                    }
                    rows.push(cellValues.join('\t'));
                }
            }
            const text = rows.join('\n');
            navigator.clipboard.writeText(text).catch(err => console.error('Copy failed:', err));

            // Blink copied cells with indigo color animation
            const copiedCells = this.elementTarget.querySelectorAll('.slick-cell.copied');
            copiedCells.forEach(cell => {
                cell.classList.add('slick-cell-copy-flash');
                setTimeout(() => {
                    cell.classList.remove('slick-cell-copy-flash');
                    cell.classList.remove('copied');
                }, 600);
            });
        });

        let resizer = new SlickResizer({
                container: `.${containerName}`, // DOM element selector, can be an ID or a class name
                // optionally define some padding and dimensions
                rightPadding: 5,    // defaults to 0
                bottomPadding: 10,  // defaults to 20
                minHeight: this.minHeightValue || 150,     // defaults to 180
                minWidth: 250,      // defaults to 300
                // you can also add some max values (none by default)
                // maxHeight: 1000
                // maxWidth: 2000
            },
            // the 2nd argument is an object and is optional
            // you could pass fixed dimensions, you can pass both height/width or a single dimension (passing both would obviously disable the auto-resize completely)
            // for example if we pass only the height (as shown below), it will use a fixed height but will auto-resize only the width
            // { height: 300 }
        );

        grid.registerPlugin(resizer);

        function slickResize() {
            let totalRowCount = dataView.filteredItems.length;
            let colGroupCount = colGroups.length > 0 ? 1 : 0;
            const tHeight = grid.getContainerNode().querySelector('.slick-header-left')?.offsetHeight || topPanelRowHeight;
            let maxHeight = 650
            let totalHeight = (rowHeight * totalRowCount) + tHeight + (topPanelRowHeight * colGroupCount);
            if (scrollBar) {
                totalHeight += 20
            } else {
                totalHeight += 5
            }
            // "Scrollbar in table" grids scroll themselves (see the
            // :not(.slick-without-scrollbar) viewport rule in
            // application.tailwind.css), so cap them to the window. Otherwise
            // only cap large grids — small tables render at their natural
            // full height so the page can scroll them, not the grid.
            if (scrollBar) maxHeight = Math.max(Math.round(window.innerHeight * 0.7), 300)
            let finalHeight = (scrollBar || totalRowCount > 200) ? Math.min(totalHeight, maxHeight) : totalHeight;
            resizer.resizeGrid(0, {height: finalHeight, minHeight: finalHeight});
        }

        // SlickResizer's own window-resize handler sizes the grid to the
        // window, floored at the server's collapsed height — after an expand
        // that clips the new rows. Re-run the row-count sizing instead.
        resizer.pauseResizer(true)
        this.onWindowResize = () => slickResize()
        window.addEventListener('resize', this.onWindowResize)

        // The frozen label pane is overflow-y:hidden, so a wheel over it
        // scrolled the grid (SlickGrid's handler) *and* the page. Keep the
        // wheel on the grid while it still has rows to scroll that way.
        // Capture phase: SlickGrid stops the event's propagation.
        this.elementTarget.addEventListener('wheel', (e) => {
            if (!e.deltaY || !e.target.closest('.slick-viewport-left')) return
            const vp = this.elementTarget.querySelector('.slick-viewport-right')
            if (!vp) return
            const room = e.deltaY > 0 ? vp.scrollHeight - vp.clientHeight - vp.scrollTop : vp.scrollTop
            if (room > 0) e.preventDefault()
        }, {capture: true, passive: false})


        // Row hover highlighting intentionally disabled — moving the mouse
        // over the grid no longer selects/colours the whole row.

        grid.onCellChange.subscribe(function (e, args) {
            dataView.updateItem(args.item.id, args.item);
        });

        grid.onActiveCellChanged.subscribe((e, args) => {
            const activeCellNode = grid.getCellNode(args.row, args.cell);
            if (activeCellNode) {
                const cellRect = activeCellNode.getBoundingClientRect();
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const headerOffset = this.isSticky ? (this.headerLeft ? this.headerLeft.offsetHeight : 0) : 0;
                const targetTop = headerOffset + 20;
                const targetBottom = viewportHeight - 20;
                if (cellRect.top < targetTop) {
                    window.scrollBy({
                        top: cellRect.top - targetTop,
                        behavior: 'smooth'
                    });
                }
                else if (cellRect.bottom > targetBottom) {
                    window.scrollBy({
                        top: cellRect.bottom - targetBottom,
                        behavior: 'smooth'
                    });
                }
            }
        })

        var getData = function (url, item, target) {
            const aborter = new AbortController()
            this.detailAborts.add(aborter)
            let safeUrl
            try { safeUrl = reportActionUrl(url, this.element) } catch (error) {
                this.detailAborts.delete(aborter)
                reportRequestFailed(error.message, this.element.dataset.reportItemId)
                return
            }
            fetch(safeUrl, {
                method: 'GET', headers: {
                    'Accept': 'application/json',
                }, signal: aborter.signal
            }).then((response) => {
                if (!response.ok) throw new Error(`Report drilldown failed (${response.status})`)
                return response.json()
            }).then(result => {
                // Platform ReportBlock drilldowns use the safe report DTO envelope;
                // legacy Story drilldowns still return the direct `{data: rows}` shape.
                let fetchedRows
                if (this.element.classList.contains('platform-report-grid')) {
                    const expectedSourceId = platformReportSourceId(this.element)
                    const responseSourceId = result?.report?.source_id
                    if (!result?.report || String(responseSourceId || '') !== expectedSourceId) {
                        throw new Error('Invalid report drilldown response')
                    }
                    fetchedRows = result.report.data?.rows
                    if (!Array.isArray(fetchedRows)) throw new Error('Invalid report drilldown response')
                } else {
                    fetchedRows = result.rows || result.data || []
                }
                dataView.beginUpdate();
                let len = dataView.getItemCount()
                for (var i = 0; i < fetchedRows.length; i++) {
                    fetchedRows[i]['indent'] = item.indent + 1
                    fetchedRows[i]['parent'] = item.id
                    fetchedRows[i]['id'] = `id_${len + i + 1}`
                    fetchedRows[i]['report_section_name'] = item['report_section_name']
                }
                target.classList.remove('lz-loader-icon', 'h-[12px]', 'w-[12px]')
                target.classList.add('lz-caret-down-icon', 'h-[10px]', 'w-[14px]')
                let index = dataView.getIdxById(item.id)
                dataView.insertItems(index + 1, fetchedRows)
                item._collapsed = false;
                item.hasChildren = true;
                dataView.updateItem(item.id, item);
                grid.updateRowCount();
                dataView.endUpdate();
                CreateAddlHeaderRow(grid, options, columns);
                slickResize()
                this.reattachStickyHeaders();
                this.handleScroll()

            }).catch(error => {
                if (error.name !== 'AbortError') {
                    target.classList.remove('lz-loader-icon')
                    target.classList.add('lz-caret-right-icon')
                    reportRequestFailed(error.message, this.element.dataset.reportItemId)
                }
            }).finally(() => this.detailAborts.delete(aborter))
        }.bind(this)

        let getSortedChildren = (array, parent) => {
            let children = array.filter(item => item.parent === parent.id).sort(comparer);
            let sortedChildren = [];
            for (let child of children) {
                sortedChildren.push(child);
                sortedChildren.push(...getSortedChildren(array, child));
            }
            return sortedChildren;
        };


        grid.onSort.subscribe((e, args) => {
            // If headers are sticky, unstick them before sorting
            if (this.isSticky) {
                this.unstickHeaders();
            }

            // With multiColumnSort on, SlickGrid hands us the whole sort order
            // in args.sortCols; fall back to the single-column shape so this
            // keeps working if the option is ever turned off.
            let clickedCols = args.sortCols || (args.sortCol ? [{sortCol: args.sortCol, sortAsc: args.sortAsc}] : []);
            let nextSortCols = clickedCols
                .filter(c => c.sortCol && c.sortCol.field)
                .map(c => ({field: c.sortCol.field, dir: c.sortAsc ? 1 : -1}));
            if (nextSortCols.length) {
                sortCols = nextSortCols;
            }
            dataView.beginUpdate();

            let pData = _(dataView.items)
                .groupBy('report_section_name')
                .mapValues(group => group.filter(item => !item.parent).sort(comparer))
                .mapValues(group => group.flatMap(root => [root, ...getSortedChildren(dataView.items, root)]))
                .toPairs()
                .sortBy(([key]) => groupNames.indexOf(key))
                .fromPairs()
                .values()
                .flatten()
                .value();

            dataView.setItems(pData);
            grid.updateRowCount();
            dataView.endUpdate();
            CreateAddlHeaderRow(grid, options, columns);

            // Reattach sticky headers after grid updates are complete
            // Use requestAnimationFrame instead of setTimeout for better timing
            requestAnimationFrame(() => {
                this.reattachStickyHeaders();
                // Check scroll position to update sticky headers if needed
                this.handleScroll();
            });
        });

        // Compare on each sorted column in turn: the first column decides, ties
        // fall through to the next one, and so on.
        var comparer = function (a, b) {
            for (let i = 0; i < sortCols.length; i++) {
                let {field, dir} = sortCols[i];
                let x = a[`${field}_sort_value`], y = b[`${field}_sort_value`];
                if (x !== y) {
                    return dir * (x > y ? 1 : -1);
                }
            }
            return 0;
        }

        var checkItemClick = function (args, target) {
            var item = dataView.getItem(args.row);
            if (item) {
                if (!item._collapsed) {
                    item._collapsed = true;
                    dataView.updateItem(item.id, item);
                    CreateAddlHeaderRow(grid, options, columns);
                    slickResize();
                    this.reattachStickyHeaders();
                    this.handleScroll()
                } else {
                    if (item.hasChildren) {
                        item._collapsed = false;
                        dataView.updateItem(item.id, item);
                        CreateAddlHeaderRow(grid, options, columns);
                        slickResize();
                        this.reattachStickyHeaders();
                        this.handleScroll()
                    } else {
                        if (target.classList.contains("lz-caret-right-icon")) {
                            target.classList.remove('lz-caret-right-icon', 'h-[10px]', 'w-[14px]')
                            target.classList.add('lz-loader-icon', 'h-[12px]', 'w-[12px]')
                            getData(item.tree_link, item, target)
                        }
                    }
                }
            }
        }.bind(this)

        grid.onClick.subscribe(function (e, args) {
            var target = e.getNativeEvent().target
            if (target.classList.contains("toggle")) {
                checkItemClick(args, target)
                e.stopImmediatePropagation();
            }
        });

        dataView.onRowCountChanged.subscribe(function (e, args) {
            grid.updateRowCount();
            grid.render();
        });

        dataView.onRowsChanged.subscribe(function (e, args) {
            grid.invalidateRows(args.rows);
            grid.render();
        });

        CreateAddlHeaderRow(grid, options, columns);

        grid.onColumnsResized.subscribe(function (e) {
            CreateAddlHeaderRow(grid, options, columns);
        });

        // ── Auto-fit column width on double-click ────────────────────────────
        // SlickGrid already fires onColumnsResizeDblClick when the border between
        // two headers is double-clicked, but its built-in autosizeColumn() only
        // computes a number into column.autoSize.widthPx and never applies it —
        // so we measure and apply ourselves. Measurement runs the real formatter
        // and then strips tags, because numeric cells hold a compact currency
        // placeholder rather than the text the user actually sees.
        // There is deliberately no upper bound on the fitted width — a
        // double-clicked column grows to whatever its longest value needs and
        // you scroll sideways to read it, exactly like Excel. The single
        // exception is frozen columns, capped below.
        const AUTOFIT_MIN = 60
        const AUTOFIT_FROZEN_SHARE = 0.6   // frozen block may claim at most this much of the grid width
        const AUTOFIT_CELL_PADDING = 16    // .slick-cell left+right padding + border
        const AUTOFIT_HEADER_PADDING = 28  // header padding + sort/menu icon room
        const AUTOFIT_MAX_ROWS = 10000     // keep the double-click instant on huge reports
        const AUTOFIT_EDGE_ZONE = 8        // px from a header's right edge that counts as "on the border"

        const measureCtx = document.createElement('canvas').getContext('2d')
        const plainText = (html) => {
            if (html == null) return ''
            if (typeof html !== 'string') return String(html)
            return html.replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim()
        }

        const autoFitColumn = (colIdx) => {
            const cols = grid.getColumns()
            const col = cols[colIdx]
            if (!col) return

            const sampleCell = this.elementTarget.querySelector('.slick-cell')
            const cs = sampleCell ? getComputedStyle(sampleCell) : null
            measureCtx.font = cs ? `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}` : '13px sans-serif'

            let widest = 0
            const rowCount = Math.min(dataView.getLength(), AUTOFIT_MAX_ROWS)
            for (let r = 0; r < rowCount; r++) {
                const item = dataView.getItem(r)
                if (!item) continue
                let out
                try {
                    out = col.formatter
                        ? col.formatter(r, colIdx, item[col.field], col, item, grid)
                        : item[col.field]
                } catch (err) {
                    // a single unformattable row must not abort the whole measurement
                    out = item[col.field]
                }
                const text = plainText(out && out.html !== undefined ? out.html : out)
                let w = measureCtx.measureText(text).width
                // the first column renders a tree indent + collapse caret that the
                // stripped text can't account for
                if (colIdx === 0) {
                    w += (item.indent || 0) * 15 + (item.tree_link !== undefined ? 22 : 0)
                }
                if (w > widest) widest = w
            }
            widest += AUTOFIT_CELL_PADDING

            // never clip the header label itself
            const nameEl = grid.getHeaderColumn(colIdx)?.querySelector('.slick-column-name')
            if (nameEl) widest = Math.max(widest, nameEl.scrollWidth + AUTOFIT_HEADER_PADDING)

            let width = Math.ceil(Math.max(widest, col.minWidth || AUTOFIT_MIN))
            if (col.maxWidth) width = Math.min(width, col.maxWidth)

            // Frozen columns are the one place an unbounded width does real
            // damage: SlickGrid drops the freeze entirely (validateColumnFreeze-
            // Width -> frozenColumn = -1) the moment the frozen block is wider
            // than the grid container, so a long value would silently unpin the
            // columns instead of widening them. Keep the frozen block inside its
            // share of the grid; every other column is free to grow.
            const frozenIdx = grid.getOptions().frozenColumn
            if (frozenIdx >= 0 && colIdx <= frozenIdx) {
                const containerWidth = this.elementTarget.clientWidth || 0
                if (containerWidth > 0) {
                    let siblingsWidth = 0
                    for (let i = 0; i <= frozenIdx && i < cols.length; i++) {
                        if (i !== colIdx && cols[i] && !cols[i].hidden) siblingsWidth += cols[i].width || 0
                    }
                    const budget = Math.floor(containerWidth * AUTOFIT_FROZEN_SHARE) - siblingsWidth
                    if (budget > (col.minWidth || AUTOFIT_MIN)) width = Math.min(width, budget)
                }
            }

            if (width === col.width) return

            // Two separate "spread the columns across the viewport" passes used
            // to wipe out the width we just measured, which is why the
            // double-click looked like it did nothing at all:
            //   * forceFitColumns pins the grid's autosizeColsMode to
            //     LegacyForceFit, and turning forceFitColumns off does NOT
            //     reset that mode, so resizeCanvas() kept re-fitting;
            //   * SlickResizer.resizeGrid() calls grid.autosizeColumns()
            //     whenever enableAutoSizeColumns is set — which it is — and
            //     that redistributes every width too.
            // The user has taken manual control of the widths now, so switch
            // both passes off before applying the new width.
            const gridOpts = grid.getOptions()
            if (gridOpts.forceFitColumns || gridOpts.enableAutoSizeColumns || gridOpts.autosizeColsMode !== 'NOA') {
                grid.setOptions({
                    forceFitColumns: false,
                    enableAutoSizeColumns: false,
                    autosizeColsMode: 'NOA' // GridAutosizeColsMode.None
                })
            }

            col.width = width
            grid.setColumns(cols)
            CreateAddlHeaderRow(grid, options, cols)
            // Wrapped-label observation adjusts height after this width change;
            // avoid another autosize pass here, which would undo the fitted width.
        }

        // The library event and the container listener below can both fire for
        // the same double-click; measuring twice is wasted work, so collapse
        // them into one run.
        let lastAutoFitAt = -1e9
        const requestAutoFit = (colIdx) => {
            const now = (window.performance && performance.now) ? performance.now() : 0
            if (now - lastAutoFitAt < 400) return
            lastAutoFitAt = now
            autoFitColumn(colIdx)
        }

        grid.onColumnsResizeDblClick.subscribe((e, args) => {
            const idx = grid.getColumns().findIndex(c => String(c.id) === String(args.triggeredByColumn))
            if (idx >= 0) requestAutoFit(idx)
        })

        // SlickGrid's own onColumnsResizeDblClick only fires when both clicks
        // land on the exact same handle element, but the handles are destroyed
        // and rebuilt on every column resize — so the second click often hits a
        // brand new node and the browser reports the double-click against the
        // header instead. Listen on the grid container as well (it outlives
        // every re-render) and accept either the handle itself or a click
        // inside the handle's strip at the header's right edge. Anything
        // further into the header is left alone so ordinary click-to-sort keeps
        // working.
        const autoFitTargetIndex = (ev) => {
            if (!ev.target || !ev.target.closest) return -1
            let headerEl = ev.target.closest('.slick-resizable-handle')?.parentElement
            if (!headerEl) {
                const candidate = ev.target.closest('.slick-header-column')
                // column-group cells in the pre-header panel reuse the same
                // class but carry no dataset id — they aren't resizable
                if (candidate && candidate.dataset && candidate.dataset.id !== undefined) {
                    const rect = candidate.getBoundingClientRect()
                    if (ev.clientX >= rect.right - AUTOFIT_EDGE_ZONE) headerEl = candidate
                }
            }
            const colId = headerEl?.dataset?.id
            if (colId === undefined) return -1
            return grid.getColumns().findIndex(c => String(c.id) === String(colId))
        }

        if (this.autoFitDblClickHandler) {
            this.elementTarget.removeEventListener('dblclick', this.autoFitDblClickHandler)
        }
        this.autoFitDblClickHandler = (ev) => {
            const idx = autoFitTargetIndex(ev)
            if (idx < 0) return
            ev.preventDefault()
            requestAutoFit(idx)
        }
        this.elementTarget.addEventListener('dblclick', this.autoFitDblClickHandler)

        grid.onKeyDown.subscribe((e, args) => {
            const row = args.row;
            const cell = args.cell;
            const dataLength = grid.getDataLength();
            const isCtrlCmd = e.ctrlKey || e.metaKey;
            if (grid.getEditorLock().isActive()) {
                return;
            }
            if (isCtrlCmd && e.key === "ArrowDown") {
                if (dataLength > 0) {
                    const lastRow = dataLength - 1;
                    grid.gotoCell(lastRow, cell, true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            else if (isCtrlCmd && e.key === "ArrowUp") {
                if (dataLength > 0) {
                    grid.gotoCell(0, cell, true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            else if (isCtrlCmd && e.key === "ArrowRight") {
                const lastCol = grid.getColumns().length - 1;
                grid.gotoCell(row, lastCol, true);
                e.preventDefault();
                e.stopPropagation();
            }
            else if (isCtrlCmd && e.key === "ArrowLeft") {
                grid.gotoCell(row, 0, true);
                e.preventDefault();
                e.stopPropagation();
            }
            else if (e.key === 'Enter' && cell === 0 && grid.getDataItem(row) && grid.getDataItem(row).tree_link !== undefined) {
                checkItemClick(args, grid.getCellNode(row, cell).getElementsByClassName('toggle')[0]);

            }
        });

        // Cache the frozen-left panes and only toggle the shadow class on the
        // boundary transition (scrolled-from-zero vs not). querySelectorAll +
        // classList ops on every onScroll tick were causing visible jank on
        // horizontal scroll.
        let cachedLeftPanes = null
        let shadowOn = false
        grid.onScroll.subscribe((e, args) => {
            if (!cachedLeftPanes) {
                cachedLeftPanes = this.element.querySelectorAll('.slick-pane-left.frozen')
            }
            const wantShadow = args.scrollLeft > 0
            if (wantShadow === shadowOn) return
            shadowOn = wantShadow
            cachedLeftPanes.forEach(p => p.classList.toggle('shadow-visible', wantShadow))
        })

        this.grid = grid
        this.options = options
        // The column panel re-runs the height calculation after a show/hide,
        // so it needs the same resize routine the grid itself uses.
        this.slickResizeFn = slickResize

        // SlickGrid's handleClick calls getTextSelection()+setTextSelection() to preserve
        // text selection for Firefox. When a text selection exists, setTextSelection() calls
        // window.getSelection().addRange() which fires a selectionchange event. CodeMirror's
        // global selectionchange listener then crashes when view.root is unavailable.
        // Returning null here skips the addRange() call entirely.
        this.grid.getTextSelection = () => null

        // Re-format currency cells when the number-scale dropdown changes the
        // card's data-report-scale. The cells format client-side in the column
        // formatter now (no per-cell Stimulus controller), so re-rendering the
        // visible rows re-applies the new scale.
        this.cardScaleObserver = new MutationObserver(() => {
            grid.invalidateAllRows()
            grid.render()
        })
        this.cardScaleObserver.observe(card, {attributes: true, attributeFilter: ['data-report-scale']})

        slickResize()
        this.setupStickyHeaders()
        if (this.elementTarget.classList.contains('slick-fit-window')) {
            // Measure rendered labels, not character counts: font, links and
            // actual column widths all affect wrapping. Both frozen panes use
            // the tallest label, without changing the data-row height.
            const updateHeaderHeight = () => {
                const cells = this.elementTarget.querySelectorAll('.slick-header-column[data-id]')
                let height = topPanelRowHeight
                cells.forEach(cell => {
                    const label = cell.querySelector('.slick-column-name')
                    if (!label || !label.getClientRects().length) return
                    const style = getComputedStyle(cell)
                    height = Math.max(height, Math.ceil(label.getBoundingClientRect().height
                        + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
                        + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)))
                })
                const value = `${height}px`
                if (this.elementTarget.style.getPropertyValue('--lz-wrapped-header-height') === value) return
                this.elementTarget.style.setProperty('--lz-wrapped-header-height', value)
                slickResize()
                this.reattachStickyHeaders()
                this.handleScroll()
            }
            const scheduleHeaderHeight = () => {
                if (this.headerResizeFrame) return
                this.headerResizeFrame = requestAnimationFrame(() => {
                    this.headerResizeFrame = null
                    updateHeaderHeight()
                })
            }
            this.headerSizeObserver = new ResizeObserver(scheduleHeaderHeight)
            this.elementTarget.querySelectorAll('.slick-header-column[data-id] .slick-column-name')
                .forEach(label => this.headerSizeObserver.observe(label))
            grid.onHeaderCellRendered.subscribe((_event, args) => {
                const label = args.node.querySelector('.slick-column-name')
                if (label) this.headerSizeObserver.observe(label)
                scheduleHeaderHeight()
            })
            grid.onBeforeHeaderCellDestroy.subscribe((_event, args) => {
                const label = args.node.querySelector('.slick-column-name')
                if (label) this.headerSizeObserver.unobserve(label)
            })
            updateHeaderHeight()
        }
    }



    setupStickyHeaders() {
        // Store references to the header elements
        this.headerLeft = this.elementTarget.querySelector('.slick-pane-header.slick-pane-left');
        this.headerRight = this.elementTarget.querySelector('.slick-pane-header.slick-pane-right');

        // Store the grid container reference
        this.gridContainer = this.elementTarget;

        // Create placeholder elements
        this.leftPlaceholder = document.createElement('div');
        this.rightPlaceholder = document.createElement('div');

        // Flag to track if headers are currently sticky
        this.isSticky = false;

        // Store original header styles
        this.originalStyles = {
            left: null,
            right: null
        };

        // Bind event handlers
        this.handleScroll = this.handleScroll.bind(this);

        // rAF-coalesce the scroll handler — handleScroll does two
        // getBoundingClientRect calls (forced layout) per invocation, and the
        // previous 10ms throttle was firing it ~100×/sec, which is what made
        // page scroll choppy when the grid has a sticky header.
        let scrollPending = false
        const onWindowScroll = () => {
            if (scrollPending) return
            scrollPending = true
            requestAnimationFrame(() => {
                scrollPending = false
                this.handleScroll()
            })
        }
        this.onWindowScroll = onWindowScroll
        window.addEventListener('scroll', this.onWindowScroll, {passive: true});

        // Initial calculation after a short delay to ensure grid is fully rendered
        this.headerInitTimeout = setTimeout(() => this.initializeHeaderTracking(), 300);
    }
    initializeHeaderTracking() {
        if (!this.headerLeft || !this.headerRight) return;

        // Save original positions and styles
        const leftRect = this.headerLeft.getBoundingClientRect();
        const rightRect = this.headerRight.getBoundingClientRect();

        // Original styles are captured in makeHeadersSticky() at stick-time, not
        // here, so they can never snapshot our own sticky values.

        // Set up placeholder dimensions
        this.leftPlaceholder.style.height = `${leftRect.height}px`;
        this.leftPlaceholder.style.width = `${leftRect.width}px`;
        this.leftPlaceholder.style.display = 'none';

        this.rightPlaceholder.style.height = `${rightRect.height}px`;
        this.rightPlaceholder.style.width = `${rightRect.width}px`;
        this.rightPlaceholder.style.display = 'none';

        // Append placeholders to the DOM
        if (this.headerLeft.parentNode) {
            this.headerLeft.parentNode.insertBefore(this.leftPlaceholder, this.headerLeft);
        }

        if (this.headerRight.parentNode) {
            this.headerRight.parentNode.insertBefore(this.rightPlaceholder, this.headerRight);
        }

        // Initial position check
        this.handleScroll();
    }

    throttle(func, limit) {
        let lastFunc;
        let lastRan;
        return function() {
            const context = this;
            const args = arguments;
            if (!lastRan) {
                func.apply(context, args);
                lastRan = Date.now();
            } else {
                clearTimeout(lastFunc);
                lastFunc = setTimeout(function() {
                    if ((Date.now() - lastRan) >= limit) {
                        func.apply(context, args);
                        lastRan = Date.now();
                    }
                }, limit - (Date.now() - lastRan));
            }
        };
    }

    handleScroll() {
        // If SlickGrid rebuilt its panes underneath us, the pane we pinned is
        // now detached — drop the stale sticky state and re-grab the live panes.
        // Otherwise we'd early-return forever and leave an orphaned fixed header
        // stuck at the top of the page.
        if (this.headerLeft && !this.headerLeft.isConnected) {
            this.isSticky = false;
            this.headerLeft = this.gridContainer && this.gridContainer.querySelector('.slick-pane-header.slick-pane-left');
            this.headerRight = this.gridContainer && this.gridContainer.querySelector('.slick-pane-header.slick-pane-right');
        }
        if (!this.headerLeft || !this.headerRight || !this.gridContainer) return;

        // Get current grid position
        const gridRect = this.gridContainer.getBoundingClientRect();
        const headerRect = this.headerLeft.getBoundingClientRect();
        const scrollY = window.scrollY;

        // Grid is in view and header should be sticky
        const headerHeight = headerRect.height;
        const shouldBeSticky = gridRect.top < 0 && gridRect.bottom > headerHeight;

        if (shouldBeSticky && !this.isSticky) {
            this.makeHeadersSticky();
        } else if (!shouldBeSticky && this.isSticky) {
            this.unstickHeaders();
        }

        // When sticky, we need to handle the case where we're scrolling past the grid
        if (this.isSticky) {
            // If we've scrolled past the bottom of the grid minus header height,
            // we need to position the headers absolutely rather than fixed
            if (gridRect.bottom < headerHeight) {
                this.headerLeft.style.position = 'absolute';
                this.headerLeft.style.top = `${gridRect.height - headerHeight}px`;

                this.headerRight.style.position = 'absolute';
                this.headerRight.style.top = `${gridRect.height - headerHeight}px`;
            } else {
                // Otherwise keep them fixed at top: 0
                this.headerLeft.style.position = 'fixed';
                this.headerLeft.style.top = '0';

                this.headerRight.style.position = 'fixed';
                this.headerRight.style.top = '0';
            }
        }
    }

    makeHeadersSticky() {
        if (this.isSticky) return;
        this.isSticky = true;

        // Capture SlickGrid's real layout inline styles *right before* we
        // overwrite them, so unstickHeaders() always restores the true values.
        // Capturing at init/reattach instead could snapshot a stale 'fixed'
        // value (Turbo cache bakes inline styles), which permanently pinned the
        // header at top:0.
        this.originalStyles = {
            left: {
                position: this.headerLeft.style.position,
                top: this.headerLeft.style.top,
                width: this.headerLeft.style.width,
                zIndex: this.headerLeft.style.zIndex
            },
            right: {
                position: this.headerRight.style.position,
                top: this.headerRight.style.top,
                width: this.headerRight.style.width,
                zIndex: this.headerRight.style.zIndex,
                left: this.headerRight.style.left
            }
        };

        // Show placeholders
        this.leftPlaceholder.style.display = 'block';
        this.rightPlaceholder.style.display = 'block';

        // Save current positions and sizes
        const leftRect = this.headerLeft.getBoundingClientRect();
        const rightRect = this.headerRight.getBoundingClientRect();

        // Make headers fixed
        this.headerLeft.style.position = 'fixed';
        this.headerLeft.style.top = '0';
        this.headerLeft.style.zIndex = '1000';
        this.headerLeft.style.width = `${leftRect.width}px`;
        const worksheet = this.element.classList.contains('lz-report-worksheet');
        this.headerLeft.style.backgroundColor = 'var(--lz-report-header-bg, #eef2ff)';

        this.headerRight.style.position = 'fixed';
        this.headerRight.style.top = '0';
        this.headerRight.style.zIndex = '1000';
        this.headerRight.style.left = `${rightRect.left}px`;
        this.headerRight.style.width = `${rightRect.width}px`;
        this.headerRight.style.backgroundColor = 'var(--lz-report-header-bg, #eef2ff)';

        // Add shadow for visual indication
        const shadow = worksheet ? '0 3px 6px -3px rgba(0,0,0,.13)' : '0 4px 10px -3px rgba(79,70,229,0.18)';
        this.headerLeft.style.boxShadow = shadow;
        this.headerRight.style.boxShadow = shadow;
    }

    unstickHeaders() {
        if (!this.isSticky) return;
        this.isSticky = false;

        // Hide placeholders
        this.leftPlaceholder.style.display = 'none';
        this.rightPlaceholder.style.display = 'none';

        // Restore original styles
        if (this.originalStyles.left) {
            this.headerLeft.style.position = this.originalStyles.left.position;
            this.headerLeft.style.top = this.originalStyles.left.top;
            this.headerLeft.style.zIndex = this.originalStyles.left.zIndex;
            this.headerLeft.style.width = this.originalStyles.left.width;
        }

        if (this.originalStyles.right) {
            this.headerRight.style.position = this.originalStyles.right.position;
            this.headerRight.style.top = this.originalStyles.right.top;
            this.headerRight.style.zIndex = this.originalStyles.right.zIndex;
            this.headerRight.style.left = this.originalStyles.right.left;
            this.headerRight.style.width = this.originalStyles.right.width;
        }

        // Remove shadows
        this.headerLeft.style.boxShadow = '';
        this.headerRight.style.boxShadow = '';

        // Remove background color
        this.headerLeft.style.backgroundColor = '';
        this.headerRight.style.backgroundColor = '';
    }

    disconnect() {
        if (this.headerSizeObserver) this.headerSizeObserver.disconnect()
        if (this.headerResizeFrame) cancelAnimationFrame(this.headerResizeFrame)
        this.headerResizeFrame = null
        // Clean up event listeners
        if (this.onWindowScroll) window.removeEventListener('scroll', this.onWindowScroll);
        if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
        if (this.headerInitTimeout) clearTimeout(this.headerInitTimeout)
        if (this.scrollInterval) clearInterval(this.scrollInterval)
        if (this.detailAborts) this.detailAborts.forEach(aborter => aborter.abort())

        // Stop watching the card's number-scale attribute
        if (this.scaleObserver) {
            this.scaleObserver.disconnect();
        }
        if (this.cardScaleObserver) {
            this.cardScaleObserver.disconnect();
        }

        // If headers are sticky, unstick them
        if (this.isSticky) {
            this.unstickHeaders();
        }

        // Remove placeholders
        if (this.leftPlaceholder && this.leftPlaceholder.parentNode) {
            this.leftPlaceholder.parentNode.removeChild(this.leftPlaceholder);
        }

        if (this.rightPlaceholder && this.rightPlaceholder.parentNode) {
            this.rightPlaceholder.parentNode.removeChild(this.rightPlaceholder);
        }

        // Call the original disconnect
        if (this.grid) this.grid.destroy();
    }

    reattachStickyHeaders() {
        // Reset sticky state
        if (this.isSticky) {
            this.unstickHeaders();
        }

        // Get fresh references to header elements
        this.headerLeft = this.elementTarget.querySelector('.slick-pane-header.slick-pane-left');
        this.headerRight = this.elementTarget.querySelector('.slick-pane-header.slick-pane-right');

        // Re-initialize if needed
        if (this.headerLeft && this.headerRight) {
            // Update placeholder dimensions
            const leftRect = this.headerLeft.getBoundingClientRect();
            const rightRect = this.headerRight.getBoundingClientRect();

            this.leftPlaceholder.style.height = `${leftRect.height}px`;
            this.leftPlaceholder.style.width = `${leftRect.width}px`;

            this.rightPlaceholder.style.height = `${rightRect.height}px`;
            this.rightPlaceholder.style.width = `${rightRect.width}px`;
            // originalStyles is captured at stick-time in makeHeadersSticky().
        }
    }

    CreateAddlHeaderRow(grid, options, columns) {
        // when it's a frozen grid, we need to render both side (left/right)
        if (options.frozenColumn >= 0) {
            // Add column groups to left panel
            let preHeaderPanelElm = grid.getPreHeaderPanelLeft();
            renderHeaderGroups(preHeaderPanelElm, 0, options.frozenColumn + 1, columns.length);

            // Add column groups to right panel
            preHeaderPanelElm = grid.getPreHeaderPanelRight();
            renderHeaderGroups(preHeaderPanelElm, options.frozenColumn + 1, columns.length);
        } else {
            // when it's a regular grid (without frozen rows), after clearning frozen columns, we re-render everything on the left
            let preHeaderPanelElm = grid.getPreHeaderPanelLeft();
            renderHeaderGroups(preHeaderPanelElm, 0, columns.length);
        }


        function renderHeaderGroups(preHeaderPanel, start, end) {
            preHeaderPanel.innerHTML = '';
            preHeaderPanel.classList.add("slick-header-columns");
            preHeaderPanel.style.left = '-1000px';
            preHeaderPanel.style.width = `${grid.getHeadersWidth()}px`;
            preHeaderPanel.parentElement.classList.add("slick-header");

            let headerColumnWidthDiff = grid.getHeaderColumnWidthDiff();
            let m, header, lastColumnGroup = '', widthTotal = 0;
            const columns = grid.getColumns();

            for (let i = start; i < end; i++) {
                m = columns[i];
                if (m) {
                    if (lastColumnGroup === m.columnGroup && i > start) {
                        widthTotal += m.width;
                        header.style.width = `${widthTotal - headerColumnWidthDiff}px`;
                    } else {
                        widthTotal = m.width;
                        header = document.createElement('div');
                        header.className = 'ui-state-default slick-header-column text-center';
                        header.innerHTML = `<span class="slick-column-name">${(m.columnGroup || '')}</span>`;
                        header.style.width = `${m.width - headerColumnWidthDiff}px`;
                        preHeaderPanel.appendChild(header);
                    }
                    lastColumnGroup = m.columnGroup;
                }
            }

        }

    }

    // Raw, sign-correct numeric value of a cell. `_sort_value` is built by
    // TabulatorHelper to equal the cell's `data-currency-amount-value`
    // (unscaled, expense-sign applied), so it is the canonical figure to sum.
    cellNumber(item, field) {
        const raw = item[`${field}_sort_value`];
        if (raw === undefined || raw === null || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseFloat(raw);
        return isFinite(num) ? num : null;
    }

    // Mirrors currency_controller's scale/comma/precision logic so the summed
    // figure matches the on-screen cells, including the live "₹ Lakhs" scale
    // toggle (written as data-report-scale on the card) and per-cell overrides.
    SCALE_DIVISORS = { units: 1, thousands: 1000, lakhs: 100000, millions: 1000000, crores: 10000000 }

    metaContent(name) {
        return document.querySelector(`meta[name="${name}"]`)?.content
    }

    isBlankAttr(v) {
        return v === null || v === undefined || v === '' || v === 'null' || v === 'undefined'
    }

    reportScaleOverride() {
        const scale = this.card && this.card.getAttribute('data-report-scale')
        return (scale && scale !== '' && scale !== 'default') ? scale : null
    }

    resolveScale(cellSpan) {
        let scale = this.reportScaleOverride()
        if (this.isBlankAttr(scale)) scale = cellSpan && cellSpan.getAttribute('data-currency-number-scale-value')
        if (this.isBlankAttr(scale)) scale = this.metaContent('userRoundPreference') || 'units'
        return scale
    }

    resolveCommaLocale(cellSpan) {
        let style = cellSpan && cellSpan.getAttribute('data-currency-comma-style-value')
        if (this.isBlankAttr(style)) style = this.metaContent('userCommaStyle') || 'indian'
        return style === 'western' ? 'en-US' : 'en-IN'
    }

    resolvePrecision(cellSpan, scale) {
        // A scale override forces precision to come from the user default.
        let p = this.reportScaleOverride() ? '' : (cellSpan && cellSpan.getAttribute('data-currency-precision-value'))
        if (this.isBlankAttr(p)) p = this.metaContent('userPrecision') || ''
        if (p === '') return scale === 'units' ? 0 : 2
        return parseInt(p)
    }

    formatSum(amount, cellSpan) {
        const scale = this.resolveScale(cellSpan)
        const locale = this.resolveCommaLocale(cellSpan)
        const precision = this.resolvePrecision(cellSpan, scale)
        const divisor = this.SCALE_DIVISORS[scale] || 1
        const scaled = parseFloat((Math.abs(amount) / divisor).toFixed(precision))
        const formatted = new Intl.NumberFormat(locale, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision
        }).format(scaled)
        return amount < 0 ? `(${formatted})` : formatted
    }

    ensureStatusBar() {
        if (this.statusBar) return this.statusBar;

        // The report card carries the live data-report-scale used for currency
        // formatting; fall back to the controller element when there is no card.
        this.card = this.element.querySelector('.lz-report-card') || this.element;

        const bar = document.createElement('div');
        bar.style.display = 'none';
        bar.className = 'slick-selection-stats items-center justify-end gap-2 ' +
            'px-3 py-1.5 bg-indigo-50 border-t border-indigo-100 rounded-b-xl';
        bar.innerHTML =
            '<span class="slick-stat-count inline-flex items-center gap-1 rounded-md bg-white ' +
            'px-2 py-0.5 text-xs font-medium text-gray-600 border border-indigo-100"></span>' +
            '<span class="slick-stat-sum inline-flex items-center gap-1 rounded-md bg-indigo-600 ' +
            'px-2 py-0.5 text-xs font-semibold text-white shadow-sm"></span>';

        this.card.appendChild(bar);

        // The scale dropdown rewrites data-report-scale on the card without
        // touching the selection, so recompute the sum when it changes while a
        // selection is active.
        this.scaleObserver = new MutationObserver(() => {
            if (this.lastRanges) {
                this.updateSelectionStats(this.lastRanges, this.grid, this.grid.getData());
            }
        });
        this.scaleObserver.observe(this.card, {
            attributes: true,
            attributeFilter: ['data-report-scale']
        });

        this.statusBar = bar;
        return bar;
    }

    updateSelectionStats(ranges, grid, dataView) {
        const bar = this.ensureStatusBar();
        this.lastRanges = ranges;

        if (!ranges || ranges.length === 0) {
            bar.style.display = 'none';
            return;
        }

        let cellCount = 0;
        let numericCount = 0;
        let sum = 0;
        let cellSpan = null; // a representative currency span for formatting attrs
        const columns = grid.getColumns();

        for (const range of ranges) {
            for (let r = range.fromRow; r <= range.toRow; r++) {
                const item = dataView.getItem(r);
                if (!item) continue;
                for (let c = range.fromCell; c <= range.toCell; c++) {
                    const col = columns[c];
                    if (!col) continue;
                    cellCount++;
                    const num = this.cellNumber(item, col.field);
                    if (num !== null) {
                        numericCount++;
                        sum += num;
                        if (!cellSpan) {
                            const node = grid.getCellNode(r, c);
                            // Currency cells are now formatter-built `.currency`
                            // spans (no data-controller); still carry the
                            // data-currency-* attrs the sum reads.
                            cellSpan = node && node.querySelector('.currency');
                        }
                    }
                }
            }
        }

        // Nothing meaningful to show for a single cell.
        if (cellCount <= 1) {
            bar.style.display = 'none';
            return;
        }

        bar.querySelector('.slick-stat-count').textContent = `Count ${cellCount}`;

        const sumEl = bar.querySelector('.slick-stat-sum');
        if (numericCount > 0) {
            sumEl.textContent = `Sum ${this.formatSum(sum, cellSpan)}`;
            sumEl.style.display = '';
        } else {
            sumEl.style.display = 'none';
        }

        bar.style.display = 'flex';
    }

    heyCopy(randStr) {
        let table = document.createElement('table');

        table.id = randStr;

        // The body below is built from the grid's *visible* columns, so the
        // heading rows have to be too — otherwise hiding a column leaves the
        // copied table with more headings than data. With nothing hidden the
        // original header block is still used, so ordinary copies are unchanged.
        const anyHidden = Array.isArray(this.slickHeaders) && this.slickHeaders.some(c => c.hidden)
        if (anyHidden) {
            this.appendVisibleHeaderRows(table, this.grid.getColumns())
        } else {
            this.ogHeadersValue.forEach(rows => {
                let row = table.insertRow();
                rows.forEach(header => {
                    if (header.value !== undefined) {
                        let cell = row.insertCell();
                        cell.textContent = header.value || '';
                        if (_.has(header, 'rowspan')) {
                            cell.rowSpan = header.rowspan;
                        }
                        if (_.has(header, 'colspan')) {
                            cell.colSpan = header.colspan;
                        }
                    }
                })
            })
        }

        const scaleOverride = this.reportScaleOverride()
        this.grid.getData().items.forEach(rowData => {
            let row = table.insertRow();
            this.grid.getColumns().forEach(col => {
                let cell = row.insertCell();
                let vals = [scaledCopyValue(rowData[col.field], scaleOverride), rowData[`${col.field}_copy_value`], rowData[`${col.field}_sort_value`], rowData[col.field], '']
                cell.innerHTML = _.find(vals, (value) => value !== null && value !== undefined);
            });
        });

        table.classList.add('hidden')
        document.body.appendChild(table);


    }

    // Rebuilds the copied table's heading rows from the visible columns:
    // a band row (one cell per run of columns sharing a column group) plus the
    // column-name row. An ungrouped column spans both rows, exactly as the
    // server-rendered header block does.
    appendVisibleHeaderRows(table, columns) {
        const bandOf = (col) => (col.columnGroupName == null ? '' : String(col.columnGroupName))
        const labelOf = (col) => (col.dValue == null ? '' : String(col.dValue))
        const hasBands = columns.some(col => bandOf(col) !== '')

        if (!hasBands) {
            const row = table.insertRow()
            columns.forEach(col => { row.insertCell().textContent = labelOf(col) })
            return
        }

        const bandRow = table.insertRow()
        let i = 0
        while (i < columns.length) {
            const band = bandOf(columns[i])
            const cell = bandRow.insertCell()
            if (band === '') {
                cell.textContent = labelOf(columns[i])
                cell.rowSpan = 2
                i += 1
                continue
            }
            let span = 1
            while (i + span < columns.length && bandOf(columns[i + span]) === band) span++
            cell.textContent = band
            if (span > 1) cell.colSpan = span
            i += span
        }

        const nameRow = table.insertRow()
        columns.forEach(col => {
            // ungrouped columns already printed their label above, spanning down
            if (bandOf(col) === '') return
            nameRow.insertCell().textContent = labelOf(col)
        })
    }

    copy(e) {
        let ele = undefined
        if (e.target.classList.contains('lz-copy-icon')) {
            ele = e.target
        } else {
            ele = e.target.children[0]
        }
        ele.classList.remove('lz-copy-icon')
        ele.classList.add('lz-loader-icon')
        let randStr = (Math.random() + 1).toString(36).substring(7);
        this.heyCopy(randStr)

        const table = document.getElementById(randStr)
        const html = table.outerHTML.replace(/href=/g, '')
        // Tab-separated fallback for editors that only read text/plain. Strip
        // tabs/newlines inside a cell so they don't break the row/column grid.
        const tsv = Array.from(table.rows).map(row =>
            Array.from(row.cells).map(c => (c.textContent || '').replace(/[\t\r\n]+/g, ' ')).join('\t')
        ).join('\n')
        table.remove()

        // Put BOTH a text/html flavour (so LibreOffice Calc, Google Sheets,
        // Excel and Word parse it into cells) and a text/plain TSV flavour
        // (plain editors) on the clipboard. The old writeText() only wrote
        // text/plain HTML markup, which only Excel heuristically parsed.
        this.writeClipboard(html, tsv)
            .then(() => {
                ele.classList.remove('lz-loader-icon')
                ele.classList.add('lz-check-icon')
                _.delay(function () {
                    ele.classList.remove('lz-check-icon')
                    ele.classList.add('lz-copy-icon')
                }, 3000)
            })
            .catch(error => {
                console.error('Error copying table data to clipboard: ', error)
                ele.classList.remove('lz-loader-icon')
                ele.classList.add('lz-copy-icon')
            })
    }

    // Writes rich (text/html) + plain (text/plain) clipboard flavours so the
    // table pastes as cells in every spreadsheet, not just Excel. Falls back to
    // writeText for browsers without ClipboardItem.
    writeClipboard(html, text) {
        if (navigator.clipboard && window.ClipboardItem) {
            const item = new ClipboardItem({
                'text/html': new Blob([html], {type: 'text/html'}),
                'text/plain': new Blob([text], {type: 'text/plain'})
            })
            return navigator.clipboard.write([item])
        }
        return navigator.clipboard.writeText(text)
    }



    collapseAll() {
        let dataView = this.grid.getData()
        dataView.beginUpdate()
        dataView.items.forEach(rowData => {
            if (_.has(rowData, '_collapsed')) {
                rowData._collapsed = true
                dataView.updateItem(rowData.id, rowData)
            }
        })
        dataView.endUpdate()
    }

    moveRight() {
        this.grid.navigateNext()
    }

    startScrollLeft(speed) {
        if (this.scrollInterval) return; // Avoid multiple intervals

        this.scrollInterval = setInterval(() => {
            this.moveLeft();
        }, 100); // Adjust interval for smoother/faster scrolling
    }

    stopScrollLeft() {
        if (this.scrollInterval) {
            clearInterval(this.scrollInterval);
            this.scrollInterval = null;
        }
    }

    startScrollRight(speed) {
        if (this.scrollInterval) return; // Avoid multiple intervals

        this.scrollInterval = setInterval(() => {
            this.moveRight();
        }, 100); // Adjust interval for smoother/faster scrolling
    }

    stopScrollRight() {
        if (this.scrollInterval) {
            clearInterval(this.scrollInterval);
            this.scrollInterval = null;
        }
    }


    moveLeft() {
        this.grid.navigatePrev()
    }


    // ── Column show/hide panel ───────────────────────────────────────────────
    // Excel-style: ticking a box only edits a draft, and nothing reaches the
    // grid until Apply. Cancel — or simply reopening the menu — throws the
    // draft away. `appliedHiddenIds` is the committed state the draft resets to.
    //
    // One panel row can cover several grid columns: a report that pivots on a
    // column dimension repeats its aggregates under every band, so the row is
    // the band and it carries every column id underneath it.
    entryIds(el) {
        return (el && el.dataset.colIds ? el.dataset.colIds.split(',') : []).filter(Boolean)
    }

    get appliedHidden() {
        if (!this._appliedHidden) this._appliedHidden = new Set()
        return this._appliedHidden
    }

    // Re-seat the grid on the currently visible columns. Column 0 is never in
    // the panel, so there is always at least one column left to render.
    applyColumnVisibility() {
        if (!this.grid || !Array.isArray(this.slickHeaders)) return
        const visible = this.slickHeaders.filter(c => !c.hidden)
        if (!visible.length) return

        this.grid.setColumns(visible)
        this.CreateAddlHeaderRow(this.grid, this.options, visible)
        this.grid.invalidateAllRows()
        this.grid.render()
        if (this.slickResizeFn) this.slickResizeFn()
        this.reattachStickyHeaders()
        this.handleScroll()
    }

    // Push a set of hidden column ids onto the grid and remember it as the
    // committed state.
    commitHiddenColumns(hiddenIds) {
        this._appliedHidden = hiddenIds
        if (Array.isArray(this.slickHeaders)) {
            this.slickHeaders.forEach(c => { c.hidden = hiddenIds.has(String(c.id)) })
        }
        this.applyColumnVisibility()
    }

    // Apply — the only place a tick reaches the table.
    applyColumns(event) {
        if (event) event.preventDefault()
        const hidden = new Set()
        this.colCheckboxTargets.forEach(box => {
            if (box.checked) return
            this.entryIds(box).forEach(id => hidden.add(id))
        })
        this.commitHiddenColumns(hidden)
    }

    // Cancel — drop the draft, leave the table alone.
    cancelColumns(event) {
        if (event) event.preventDefault()
        this.revertColumnDraft()
    }

    // Puts every tick box back to the committed state and clears the search.
    // Also fires when the menu button is clicked, so reopening never shows
    // ticks from an abandoned edit.
    revertColumnDraft() {
        if (!this.hasColCheckboxTarget) return
        const applied = this.appliedHidden
        this.colCheckboxTargets.forEach(box => {
            box.checked = !this.entryIds(box).some(id => applied.has(id))
        })
        if (this.hasColumnSearchTarget) this.columnSearchTarget.value = ''
        this.filterColumnList()
    }

    // Reset — everything back on screen, applied straight away.
    resetColumns(event) {
        if (event) event.preventDefault()
        this.colCheckboxTargets.forEach(box => { box.checked = true })
        if (this.hasColumnSearchTarget) this.columnSearchTarget.value = ''
        this.filterColumnList()
        this.commitHiddenColumns(new Set())
    }

    // Rows the search is currently showing — everything Select all acts on.
    listedRows() {
        return this.colRowTargets.filter(row => !row.classList.contains('hidden'))
    }

    listedCheckboxes() {
        return this.listedRows()
            .map(row => row.querySelector('[data-slick-grid-target="colCheckbox"]'))
            .filter(Boolean)
    }

    // Select all applies to the searched subset, so "resi" + Select all is a
    // way to act on just those columns — the same as Excel's filter list.
    toggleSelectAll(event) {
        const on = event.currentTarget.checked
        event.currentTarget.indeterminate = false
        this.listedCheckboxes().forEach(box => { box.checked = on })
    }

    syncSelectAll() {
        if (!this.hasSelectAllTarget) return
        const boxes = this.listedCheckboxes()
        const on = boxes.filter(b => b.checked).length
        this.selectAllTarget.checked = boxes.length > 0 && on === boxes.length
        this.selectAllTarget.indeterminate = on > 0 && on < boxes.length
    }

    // Search — narrows the list only; it never changes what the grid shows.
    filterColumnList() {
        const q = (this.hasColumnSearchTarget ? this.columnSearchTarget.value : '').trim().toLowerCase()
        let anyMatch = false

        this.colRowTargets.forEach(row => {
            const match = !q || (row.dataset.colSearch || '').includes(q)
            row.classList.toggle('hidden', !match)
            if (match) anyMatch = true
        })

        if (this.hasColumnEmptyTarget) {
            this.columnEmptyTarget.classList.toggle('hidden', anyMatch)
        }
        this.syncSelectAll()
    }

    // Eye icon — scrolls the column into view and flashes it. This is
    // navigation rather than a filter edit, so it acts immediately. A column
    // that is currently hidden is brought back first (and only that one — any
    // other pending ticks stay in the draft).
    jumpToColumn(event) {
        event.preventDefault()
        const ids = this.entryIds(event.currentTarget)
        if (!ids.length || !this.grid) return

        const applied = this.appliedHidden
        if (ids.some(id => applied.has(id))) {
            const next = new Set(applied)
            ids.forEach(id => next.delete(id))
            this.commitHiddenColumns(next)

            const box = this.colCheckboxTargets.find(b => this.entryIds(b).join(',') === ids.join(','))
            if (box) box.checked = true
            this.syncSelectAll()
        }

        const columns = this.grid.getColumns()
        const indexes = ids
            .map(id => columns.findIndex(c => String(c.id) === String(id)))
            .filter(i => i >= 0)
        if (!indexes.length) return

        // Flash only once the travelling has stopped: during a smooth scroll the
        // cells being scrolled towards have not been rendered yet, so
        // highlighting straight away would light up the wrong ones (or nothing).
        this.scrollColumnToStart(indexes[0], () => this.flashColumns(indexes))
    }

    // Bring a column to the *start* of the scrolling area — right beside the
    // pinned row-label column — rather than doing SlickGrid's minimum nudge,
    // which stops the moment the column's right edge appears and so leaves it
    // hard against the right of the table (and under the open panel).
    //
    // `columnPosLeft` is SlickGrid's own per-column offset table and it restarts
    // at 0 past the frozen boundary, so for any scrollable column the value is
    // already the scrollLeft we want. Both it and the scrolling container are
    // internals, so anything unexpected falls back to the built-in jump.
    scrollColumnToStart(idx, done) {
        const grid = this.grid
        const settle = () => { if (done) done() }

        const frozen = grid.getOptions ? grid.getOptions().frozenColumn : -1
        const pane = grid._viewportScrollContainerX
        const left = Array.isArray(grid.columnPosLeft) ? grid.columnPosLeft[idx] : undefined

        // A pinned column is always on screen — nothing to travel to.
        if (frozen >= 0 && idx <= frozen) return settle()

        if (!pane || typeof left !== 'number') {
            if (typeof grid.scrollColumnIntoView === 'function') {
                grid.scrollColumnIntoView(idx)
            } else if (typeof grid.scrollCellIntoView === 'function') {
                grid.scrollCellIntoView(grid.getViewport().top || 0, idx, false)
            }
            return settle()
        }

        // The last columns cannot sit at the start — the table simply runs out,
        // so clamp and let them stop as far left as the content allows.
        const max = Math.max(0, pane.scrollWidth - pane.clientWidth)
        const target = Math.max(0, Math.min(Math.round(left), max))

        if (Math.abs(pane.scrollLeft - target) < 2) return settle()

        this.onScrollSettled(pane, settle)
        if (typeof pane.scrollTo === 'function') {
            pane.scrollTo({left: target, behavior: 'smooth'})
        } else {
            pane.scrollLeft = target
        }
    }

    // Calls back once the pane stops moving. `scrollend` where it exists, and a
    // timeout everywhere else — the timeout also covers a smooth scroll that is
    // interrupted, so the highlight can never be lost entirely.
    onScrollSettled(pane, done) {
        let finished = false
        const finish = () => {
            if (finished) return
            finished = true
            pane.removeEventListener('scrollend', finish)
            done()
        }
        pane.addEventListener('scrollend', finish, {once: true})
        setTimeout(finish, 700)
    }

    // Brief highlight on the header + on-screen cells, so the eye icon lands
    // somewhere the user can actually see.
    flashColumns(indexes) {
        requestAnimationFrame(() => {
            const nodes = []
            const viewport = this.grid.getViewport()

            indexes.forEach(idx => {
                const header = this.grid.getHeaderColumn(idx)
                if (header) nodes.push(header)
                for (let r = viewport.top; r <= viewport.bottom; r++) {
                    const cell = this.grid.getCellNode(r, idx)
                    if (cell) nodes.push(cell)
                }
            })
            if (!nodes.length) return

            nodes.forEach(n => n.classList.add('slick-column-jump-flash'))
            setTimeout(() => nodes.forEach(n => n.classList.remove('slick-column-jump-flash')), 1200)
        })
    }

}
